<!--
  ResendCode — «Отправить код еще раз» с обратным отсчётом. Сервер держит паузу
  между отправками на один идентификатор (RATE_LIMIT_SECONDS в otp.service.js),
  поэтому ссылка без таймера просто ловила 429: юзер жал, ничего не менялось,
  прилетала ошибка. Компонент сам блокирует кнопку на время паузы — считаем с
  момента монтирования (шаг кода показывается сразу после отправки) и после
  каждой повторной отправки; ответ RATE_LIMITED задаёт остаток из retryAfter.
-->
<template>
  <div class="resend-code p-small d-inline-flex flex-v-center gap-small">
    <span v-if="sending" class="spinner"></span>

    <!-- Идёт пауза: микро-кольцо обратного отсчёта + число секунд. -->
    <template v-else-if="left > 0">
      <svg class="resend-code__ring" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="6" fill="none" stroke="rgba(var(--text-muted), 0.25)" stroke-width="2" />
        <circle
          class="resend-code__ring-fill"
          cx="8" cy="8" r="6" fill="none"
          stroke="rgb(var(--color-accent))" stroke-width="2" stroke-linecap="round"
          :stroke-dasharray="RING"
          :stroke-dashoffset="RING * (1 - left / cooldown)"
          transform="rotate(-90 8 8)"
        />
      </svg>
      <span class="t-muted">{{ t('auth.enterCode.resendIn', { n: left }) }}</span>
    </template>

    <!-- Пауза кончилась: ссылка повторной отправки. -->
    <button v-else type="button" class="resend-code__btn underline t-blue cursor-pointer" @click="onClick">
      {{ t('auth.enterCode.resendCode') }}
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { setError } from '@martyrs/src/modules/core/views/store/core.store.js'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  // Функция повторной отправки. Ошибку не гасит — компонент сам решает, что с ней
  // делать (RATE_LIMITED → таймер, остальное → setError).
  submit: { type: Function, required: true },
  // Пауза между отправками, секунды. Должна совпадать с серверной.
  cooldown: { type: Number, default: 30 }
})

const left = ref(0)
const sending = ref(false)
const waiting = computed(() => sending.value || left.value > 0)

// Длина окружности кольца (r=6): 2πr. Для dasharray/dashoffset обратного отсчёта.
const RING = 2 * Math.PI * 6

let timer = null

function startCountdown(seconds) {
  left.value = Math.max(0, Math.ceil(seconds || 0))
  clearInterval(timer)
  if (!left.value) return
  timer = setInterval(() => {
    left.value -= 1
    if (left.value <= 0) clearInterval(timer)
  }, 1000)
}

async function onClick() {
  if (waiting.value) return
  sending.value = true
  try {
    await props.submit()
    startCountdown(props.cooldown)
  } catch (error) {
    const data = error?.response?.data
    // Код ещё «свежий» — это не ошибка для юзера, просто дожидаемся паузы.
    if (data?.errorCode === 'RATE_LIMITED') startCountdown(data.retryAfter || props.cooldown)
    else setError(error)
  } finally {
    sending.value = false
  }
}

// Шаг с кодом открывается сразу после отправки — пауза уже идёт.
onMounted(() => startCountdown(props.cooldown))
onUnmounted(() => clearInterval(timer))
</script>

<style scoped>
.resend-code__btn {
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
}

/* Плавное «убывание» кольца по мере отсчёта. */
.resend-code__ring-fill {
  transition: stroke-dashoffset 1s linear;
}
</style>
